import { useEffect, useState } from 'react';

interface TourSpotlightProps {
  /** Viewport-relative rect to cut out of the dim layer. Null = full dim. */
  rect: DOMRect | null;
  /** Pixels of padding around the target. */
  padding?: number;
}

/**
 * A full-viewport dim layer with a transparent rectangular cutout over the
 * target element. Implemented with an SVG mask so the cutout has rounded
 * corners and animates smoothly between steps.
 *
 * The layer is `pointer-events: none` so the user can still interact with
 * whatever is behind it — including the highlighted element.
 */
export function TourSpotlight({ rect, padding = 8 }: TourSpotlightProps) {
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === 'undefined' ? 0 : window.innerWidth,
    h: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cutout = rect
    ? {
        x: Math.max(0, rect.left - padding),
        y: Math.max(0, rect.top - padding),
        w: rect.width + padding * 2,
        h: rect.height + padding * 2,
      }
    : null;

  return (
    <svg
      aria-hidden="true"
      className="fixed inset-0 z-[1000] pointer-events-none"
      width={viewport.w}
      height={viewport.h}
      style={{ width: '100vw', height: '100vh' }}
    >
      <defs>
        <mask id="ironview-tour-mask">
          {/* White = visible (dim). Black = transparent (spotlight). */}
          <rect x={0} y={0} width={viewport.w} height={viewport.h} fill="white" />
          {cutout && (
            <rect
              x={cutout.x}
              y={cutout.y}
              width={cutout.w}
              height={cutout.h}
              rx={8}
              ry={8}
              fill="black"
              style={{ transition: 'all 200ms ease' }}
            />
          )}
        </mask>
      </defs>
      <rect
        x={0}
        y={0}
        width={viewport.w}
        height={viewport.h}
        fill="rgba(0, 0, 0, 0.55)"
        mask="url(#ironview-tour-mask)"
      />
      {cutout && (
        <rect
          x={cutout.x}
          y={cutout.y}
          width={cutout.w}
          height={cutout.h}
          rx={8}
          ry={8}
          fill="none"
          stroke="rgba(255, 255, 255, 0.85)"
          strokeWidth={2}
          style={{ transition: 'all 200ms ease' }}
        />
      )}
    </svg>
  );
}
